import {
  RefObject,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { InputText } from "@sellar-io/components";
import { useLocation, useNavigate } from "react-router";
import {
  InputContainer,
  InputOption,
  InputSelect,
} from "@sellar-io/components";
import { Icon, ProfilePicture } from "../../components";
import { twMerge } from "tailwind-merge";
import {
  PriceListGetResponseType,
  ProductAssignmentListResponseType,
  ProductAssignmentProductType,
  ProductPriceTierDerivationType,
} from "@sellar-io/api-v2/types";
import * as ROUTES from "../../constants/routes";
import { useParamAsNumber } from "../../hooks/useParamAsNumber";
import { useSpotlightFilterStore } from "../spotlight/SpotlightFilterStore";
import { useShallow } from "zustand/shallow";
import { abvFilterType } from "../discovery/v2/DiscoveryV2SearchStore";
import { useDebounce } from "use-debounce";
import { SpotlightFilterContainerCard } from "../spotlight/components/SpotlightFilterContainerCard";
import { useProductAssignmentList } from "../../hooks/api/v2/product/useProductAssignmentList";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { TradeTermsLayout } from "../../components/tradeTermsAssignment/TradeTermsLayout";
import { PriceListDetail } from "./PriceListDetail";
import { usePriceList } from "../../hooks/api/v2/priceList/usePriceList";
import {
  calculatePriceTierDiscount,
  formatCurrency,
  getContainerTypes,
  getDerivedPrice,
  pluralize,
} from "@sellar-io/common";
import { PriceListPricingMap, usePriceListStore } from "./priceListStore";
import { MissingPricesDialog } from "./components/MissingPricesDialog";
import { usePriceListDelete } from "../../hooks/api/v2/priceList/usePriceListDelete";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../hooks/usePriceFormat";
import { useProductPriceListPatchV2 } from "../../hooks/api/v2/productPriceList/useProductPriceListPatchV2";
import { PriceRuleUpdateDialog } from "./components/PriceRuleUpdateDialog";
import { Button, Switch, InputCurrency } from "@sellar-io/components";
import { AlertCompact } from "../../components/alert/AlertCompact";
import { usePriceListAssign } from "../../hooks/api/v2/priceList/usePriceListAssign";
import { useFeatureFlagStore } from "../../hooks/useFeatureFlags";
import { StorefrontPreviewer } from "../../components/storefrontPreviewer/StorefrontPreviewer";
import {
  StorefrontPreviewerTabs,
  useStorefrontPreviewerParameters,
} from "../../components/storefrontPreviewer/StorefrontPreviewer.shared";
import { Alert } from "../../components/alert/Alert";
import { InfoBadge } from "../../components/badge/InfoBadge";
import {
  DiscountsPreviewTypeType,
  PriceListContext,
  SelectedDiscountType,
  usePriceListContext,
} from "./PriceListContext";
import { useContainerWidth } from "../../hooks/useContainerWidth";
import { InactiveBadge } from "../../components/badge/InactiveBadge";
import { usePriceListGetStats } from "../../hooks/api/v2/priceList/usePriceListGetStats";
import { WhyPriceDialog } from "./components/WhyPriceDialog";
import { ListItemIconTile } from "@sellar-io/components";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { derivationTypes, generatePatchParameters } from "./PriceList.shared";

export const PriceList = () => {
  const isSupplierStorefrontFeatureFlagEnabled = useFeatureFlagStore().isActive(
    "SUPPLIER_STOREFRONT_PREVIEW",
  );
  const isUnbundlingEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingReleaseFinal",
  );
  const navigate = useNavigate();
  const { state, search: locationSearch } = useLocation();
  const priceListId = useParamAsNumber(ROUTES.PRICE_LIST, "priceListId");

  const searchParams = new URLSearchParams(locationSearch);
  const productId = searchParams.get("productId")
    ? Number(searchParams.get("productId"))
    : null;

  const returnUrl = state?.returnUrl;

  const redirectToCustomerZoneId = state?.customerZoneRedirectId ?? null;
  const launchAssignmentDialog = state?.launchAssignmentDialog ?? false;
  const isNewBase = state?.isNewBase ?? false;

  const [missingPricesDialog, setMissingPricesDialog] = useState(false);

  const [discountsPreviewEnabled, setDiscountsPreviewEnabled] = useState(false);
  const [discountsPreviewType, setDiscountsPreviewType] =
    useState<DiscountsPreviewTypeType>("unit");
  const [numberOfUnits, setNumberOfUnits] = useState<number | null>(null);
  const [minimumSpend, setMinimumSpend] = useState<number | null>(null);
  const [selectedDiscount, setSelectedDiscount] =
    useState<SelectedDiscountType>();

  const { priceListPricingMap, clearUpdatedFlags } = usePriceListStore(
    useShallow((state) => ({
      priceListPricingMap: state.priceListPricingMap,
      clearUpdatedFlags: state.clearUpdatedFlags,
    })),
  );

  const allPricesSet = useMemo(() => {
    return Object.values(priceListPricingMap).every(
      (pricing) => pricing.price !== null && pricing.price !== undefined,
    );
  }, [priceListPricingMap]);

  const priceListAssign = usePriceListAssign(priceListId);

  const { data: priceList } = usePriceList(priceListId, {
    enabled: !!priceListId,
  });

  const isBaseList = priceList?.derivationType === "base";

  const deletePriceList = usePriceListDelete(priceListId);

  const patchProductPriceLists = useProductPriceListPatchV2();

  const pricePatchParameters = generatePatchParameters(
    priceListPricingMap,
    priceListId,
  );

  const redirectUrl =
    returnUrl ||
    (redirectToCustomerZoneId
      ? `${ROUTES.CUSTOMER_ZONE.href({ customerZoneId: redirectToCustomerZoneId })}?tab=priceList`
      : ROUTES.PRICE_LISTS);

  const containerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useContainerWidth(containerRef, 950);

  const nbUpdatedPrices = pricePatchParameters.length;

  const basePriceSaveBar = !!nbUpdatedPrices && isBaseList && (
    <div
      className={twMerge(
        "fixed right-0 bottom-0 flex flex-col gap-4 border-t border-t-zinc-300 bg-white p-4",
        isDesktop ? "w-[calc(100%-400px)]" : "w-full",
      )}
    >
      <Alert
        type="success"
        message={
          <span>
            You've updated the base price for {nbUpdatedPrices}{" "}
            <span className="font-semibold">
              {pluralize("product", nbUpdatedPrices)}
            </span>
          </span>
        }
        className="w-full"
      />

      <div className="flex gap-2">
        <Button
          className="w-full"
          loading={patchProductPriceLists.isPending}
          onClick={() =>
            patchProductPriceLists.mutate(pricePatchParameters, {
              onSuccess: () => {
                clearUpdatedFlags();
              },
            })
          }
        >
          Save
        </Button>
        <Button
          className="w-full"
          loading={patchProductPriceLists.isPending}
          color="secondary"
          onClick={() =>
            patchProductPriceLists.mutate(pricePatchParameters, {
              onSuccess: () => {
                clearUpdatedFlags();
                navigate(redirectUrl);
              },
            })
          }
        >
          Save & exit
        </Button>
      </div>
    </div>
  );

  return (
    <PriceListContext
      value={{
        discountsPreviewEnabled,
        setDiscountsPreviewEnabled,
        discountsPreviewType,
        setDiscountsPreviewType,
        numberOfUnits,
        setNumberOfUnits,
        minimumSpend,
        setMinimumSpend,
        selectedDiscount,
        setSelectedDiscount,
      }}
    >
      <TradeTermsLayout
        title={priceList?.name ?? ""}
        onClickLeftIcon={() => {
          if (priceList?.derivationType === "base") {
            if (!allPricesSet) {
              setMissingPricesDialog(true);
              return;
            }
          }

          if (pricePatchParameters.length > 0) {
            patchProductPriceLists.mutate(pricePatchParameters, {
              onSuccess: () => navigate(redirectUrl),
            });
            return;
          }

          navigate(redirectUrl);
        }}
        aside={
          <div className="flex flex-1 flex-col justify-between">
            <PriceListDetail />
            {isSupplierStorefrontFeatureFlagEnabled ? (
              <StorefrontPreviewerButton />
            ) : null}
          </div>
        }
        type="price list"
        isNew={isBaseList}
        hideFooter={isBaseList}
        existingConnections={
          !isBaseList ? (priceList?.SupplierRetailerConnections ?? []) : []
        }
        existingCustomerZoneIds={priceList?.CustomerZones.map((cz) => cz.id)}
        onAssign={({
          connectionIds,
          customerZoneIds,
        }: {
          connectionIds: number[];
          customerZoneIds: number[];
        }) => {
          return new Promise((resolve) => {
            priceListAssign.mutate(
              {
                connectionIds,
                customerZoneIds,
              },
              {
                onSuccess: () => {
                  resolve({
                    success: true,
                    message: "",
                  });
                },
                onError: () => {
                  resolve({
                    success: false,
                    message: "Failed to assign Price list, please try again",
                  });
                },
              },
            );
          });
        }}
        showAssignment={launchAssignmentDialog}
        assignmentHeader={
          <>
            <span className="font-semibold">Assign price list</span>
            <p className="text-zinc-500">
              {isUnbundlingEnabled
                ? "Assign this price list to your customers. Any edits you make to this price list will affect all customers you’ve assigned it to."
                : "You can choose to assign your price list to your specified customer zones or individual customers. Any edits you make to this price list will affect all customers you've assigned it to."}
            </p>
          </>
        }
        saving={patchProductPriceLists.isPending}
        hideCustomerZones={isUnbundlingEnabled}
      >
        <div
          ref={containerRef}
          className="mx-auto flex w-full max-w-[1080px] items-center gap-2"
        >
          <PriceListContent
            priceList={priceList}
            productId={productId}
            readOnly={
              priceList?.derivationType !== "base" && !priceList?.BasePriceList
            }
          />
        </div>
        {basePriceSaveBar}
      </TradeTermsLayout>

      <MissingPricesDialog
        active={missingPricesDialog}
        setActive={setMissingPricesDialog}
        canDiscard={isNewBase && pricePatchParameters.length === 0}
        onDiscard={() => {
          deletePriceList.mutate(undefined, {
            onSuccess: () => {
              setMissingPricesDialog(false);
              navigate(redirectUrl);
            },
          });
        }}
        onDismiss={() => {
          if (pricePatchParameters.length > 0) {
            patchProductPriceLists.mutate(pricePatchParameters, {
              onSuccess: () => {
                setMissingPricesDialog(false);
                navigate(redirectUrl);
              },
            });
            return;
          }
          setMissingPricesDialog(false);
          navigate(redirectUrl);
        }}
      />
    </PriceListContext>
  );
};

export const PriceListContent = ({
  priceList,
  productId: scrollProductId,
  readOnly,
}: {
  priceList?: PriceListGetResponseType;
  productId?: number | null;
  readOnly?: boolean;
}) => {
  const { discountsPreviewEnabled } = usePriceListContext();

  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [ruleDialogActive, setRuleDialogActive] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState<
    PriceListPricingMap[number] | undefined
  >(undefined);
  const [filterPerItemDiscounts, setFilterPerItemDiscounts] = useState(false);
  const [filterNoPriceSet, setFilterNoPriceSet] = useState(false);

  const [whyPriceActive, setWhyPriceActive] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useContainerWidth(containerRef, 950);

  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const { priceListPricingMap, setPriceListPricingMap, updatePrice } =
    usePriceListStore(
      useShallow((state) => ({
        priceListPricingMap: state.priceListPricingMap,
        setPriceListPricingMap: state.setPriceListPricingMap,
        updatePrice: state.updatePrice,
      })),
    );

  const isBasePrice = priceList?.derivationType === "base";
  const isMigratedList = !isBasePrice && !priceList?.BasePriceList;

  const { data: basePriceList } = usePriceList(
    priceList?.BasePriceListId ?? 0,
    {
      enabled: !!priceList?.BasePriceListId && !isBasePrice && !isMigratedList,
    },
  );

  const perItemDiscountsCount = priceList?.perItemDiscountProductCount ?? 0;

  const { data: priceListStats } = usePriceListGetStats(priceList?.id ?? 0, {
    enabled: !!priceList?.id && isBasePrice,
  });

  const showMissingPriceAlert =
    isBasePrice && priceListStats && priceListStats.missingPricesCount > 0;
  const nbMissingPrices = priceListStats?.missingPricesCount ?? 0;

  const {
    selectedContainerTypes,
    selectedAbvTypes,
    selectedDietaryTypes,
    offersSellarCreditOnly,
    activeOnly,
  } = useSpotlightFilterStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
      offersSellarCreditOnly: state.offersSellarCreditOnly,
      activeOnly: state.activeOnly,
    })),
  );

  const productQuery = useMemo(() => {
    return [
      {
        type: "priceTierId",
        modifier: "EQUALS",
        value: priceList?.id ?? 0,
      } as const,
      ...(filterPerItemDiscounts
        ? [
            {
              type: "filterPerItemDiscounts",
              modifier: "EQUALS",
              value: true,
            } as const,
          ]
        : []),
      ...(filterNoPriceSet
        ? [
            {
              type: "filterNoPriceSet",
              modifier: "EQUALS",
              value: filterNoPriceSet,
            } as const,
          ]
        : []),
      ...(offersSellarCreditOnly
        ? [
            {
              type: "offersSellarCreditOnly",
              modifier: "EQUALS",
              value: offersSellarCreditOnly,
            } as const,
          ]
        : []),
      ...(selectedAbvTypes.length
        ? selectedAbvTypes.map((type) => abvFilterType[type].query)
        : []),
      ...(selectedDietaryTypes.length
        ? [
            {
              type: "dietaryType",
              modifier: "IN",
              value: selectedDietaryTypes,
            } as const,
          ]
        : []),
      ...(selectedContainerTypes.length
        ? [
            {
              type: "containerType",
              modifier: "IN",
              value: selectedContainerTypes as Array<
                "keg" | "cask" | "can" | "bottle" | "bag" | "bagInBox"
              >,
            } as const,
          ]
        : []),
      ...(activeOnly
        ? [
            {
              type: "activeOnly",
              modifier: "EQUALS",
              value: true,
            } as const,
          ]
        : []),
    ];
  }, [
    priceList?.id,
    offersSellarCreditOnly,
    selectedAbvTypes,
    selectedDietaryTypes,
    selectedContainerTypes,
    filterPerItemDiscounts,
    activeOnly,
    filterNoPriceSet,
  ]);

  const productListQuery = useProductAssignmentList({
    search: debouncedSearch,
    limit: 5,
    query: productQuery,
  });

  const productList = useMemo(() => {
    return (
      productListQuery.data?.pages.flatMap((page) => page.map((p) => p)) ?? []
    );
  }, [productListQuery.data]);

  const getName = (product: ProductAssignmentProductType) => {
    const nameParts = product.name?.split(" • ");
    return `${nameParts?.[1]}${nameParts?.[2] ? " • " : ""}${nameParts?.[2] ?? ""}`;
  };

  const patchProductPriceLists = useProductPriceListPatchV2();

  const savePrice = (updatedPrice: PriceListPricingMap[number]) => {
    if (!priceList?.id) return;
    const pricePatchParameters = generatePatchParameters(
      Object.fromEntries([updatedPrice].map((item) => [item.productId, item])),
      priceList.id,
    );

    patchProductPriceLists.mutate(pricePatchParameters, {
      onSuccess: () => {
        updatePrice(updatedPrice);
      },
    });
  };

  const handleRuleChange = (
    productId: number,
    derivationType: ProductPriceTierDerivationType,
  ) => {
    if (["none", "inherit"].includes(derivationType)) {
      const pricing = priceListPricingMap[productId];
      const product = productList
        .flatMap((p) => p.Products)
        .find((p) => p.id === productId);

      if (
        !priceList ||
        !pricing ||
        (pricing.basePrice === null && pricing.price === null) ||
        !product
      )
        return;

      const price = getDerivedPrice({
        basePrice: pricing.basePrice ?? pricing.price ?? 0,
        containerType: product.containerType,
        productPriceTier: {
          price: product.ProductPriceTiers[0]?.price ?? 0,
          derivationType,
          derivationValue: null,
        },
        priceTier: {
          derivationType: priceList.derivationType,
          derivationValue: priceList.derivationValue,
        },
        priceTierContainerDiscounts:
          product.ProductPriceTiers[0]?.PriceTier.PriceTierContainerDiscounts ??
          [],
      });
      const updatedPrice = {
        ...pricing,
        productId,
        derivationType,
        derivationValue: null,
        price,
        updated: true,
      };
      savePrice(updatedPrice);
      return;
    }
    setSelectedPricing(
      priceListPricingMap[productId]
        ? {
            ...priceListPricingMap[productId],
            derivationType,
            ...(derivationType === "custom" ? { derivationValue: null } : {}),
          }
        : undefined,
    );
    setRuleDialogActive(true);
  };

  const toggleDiscountable = (productId: number) => {
    const pricing = priceListPricingMap[productId];
    if (!pricing) return;

    const updatedPrice = {
      ...pricing,
      discountable: !pricing.discountable,
      updated: true,
    };

    savePrice(updatedPrice);
  };

  const updateDerivedPrice = (params: {
    derivationType: ProductPriceTierDerivationType;
    derivationValue: number | null;
    price?: number | null;
  }) => {
    if (!selectedPricing) return;
    const product = productList
      .flatMap((p) => p.Products)
      .find((p) => p.id === selectedPricing.productId);
    if (!product || selectedPricing.basePrice === null || !priceList) return;

    const shouldInherit =
      (params.derivationType === "percentage" ||
        params.derivationType === "flat") &&
      params.derivationValue === 0;

    const finalDerivationType = shouldInherit
      ? "inherit"
      : params.derivationType;
    const finalDerivationValue = shouldInherit ? null : params.derivationValue;

    const derivedPrice = getDerivedPrice({
      basePrice: selectedPricing.basePrice,
      containerType: product.containerType,
      productPriceTier: {
        price: product.ProductPriceTiers[0]?.price ?? 0,
        derivationType: finalDerivationType,
        derivationValue: finalDerivationValue,
      },
      priceTier: {
        derivationType: priceList.derivationType,
        derivationValue: priceList.derivationValue,
      },
      priceTierContainerDiscounts:
        product.ProductPriceTiers[0]?.PriceTier.PriceTierContainerDiscounts ??
        [],
    });

    const updatedPrice = {
      ...selectedPricing,
      productId: product.id,
      derivationType: finalDerivationType,
      derivationValue: finalDerivationValue,
      price:
        params.derivationType === "custom"
          ? (params.price ?? null)
          : derivedPrice,
      updated: true,
    };

    savePrice(updatedPrice);
  };

  const syncPricingMap = useEffectEvent(() => {
    const newMap: PriceListPricingMap = {};

    if (!priceList) return;

    productList.forEach((parent) => {
      parent.Products.forEach((p) => {
        if (!newMap[p.id]) {
          const existing = priceListPricingMap[p.id];

          if (
            existing &&
            existing.priceListId === priceList.id &&
            isBasePrice
          ) {
            newMap[p.id] = {
              ...existing,
              productPriceListId: p.ProductPriceTiers[0]?.id,
            };
          } else {
            const baseProductPriceList = basePriceList?.ProductPriceLists.find(
              (ppt) => ppt.ProductId === p.id,
            );
            newMap[p.id] = {
              productId: p.id,
              priceListId: priceList.id,
              productPriceListId: p.ProductPriceTiers[0]?.id,
              derivationType: p.ProductPriceTiers[0]?.derivationType ?? "none",
              derivationValue: p.ProductPriceTiers[0]?.derivationValue ?? null,
              basePrice:
                p.ProductPriceTiers[0]?.basePrice ??
                baseProductPriceList?.price ??
                null,
              price: p.ProductPriceTiers[0]?.price ?? null,
              discountable: p.ProductPriceTiers[0]?.discountable ?? true,
              updated: false,
            };
          }
        }
      });
    });
    setPriceListPricingMap(newMap);
  });

  useEffect(() => {
    syncPricingMap();
  }, [productListQuery.data, isBasePrice, priceList?.id]);

  const hasScrolledRef = useRef(false);
  const fetchNextProductPage = productListQuery.fetchNextPage;

  useEffect(() => {
    if (scrollProductId && productList.length > 0 && !hasScrolledRef.current) {
      const element = document.getElementById(`product-${scrollProductId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        hasScrolledRef.current = true;
      } else if (
        productListQuery.hasNextPage &&
        !productListQuery.isFetchingNextPage
      ) {
        fetchNextProductPage();
      }
    }
  }, [
    scrollProductId,
    productList,
    productListQuery.hasNextPage,
    productListQuery.isFetchingNextPage,
    fetchNextProductPage,
  ]);

  const setInputRef = useCallback(
    (params: { productId: number; el: HTMLInputElement | null }) => {
      inputRefs.current[String(params.productId)] = params.el;
    },
    [],
  );

  const cellClasses = "flex items-center font-semibold pl-4";
  const gridClasses = isBasePrice
    ? "grid-cols-[repeat(2,_1fr)]"
    : readOnly
      ? "grid-cols-[minmax(180px,_1fr)_repeat(2,_minmax(80px,_1fr))]"
      : discountsPreviewEnabled
        ? "grid-cols-[180px_minmax(150px,_1fr)_120px_150px_130px_170px]"
        : "grid-cols-[200px_minmax(150px,_1fr)_120px_180px_130px]";

  return (
    <>
      <div ref={containerRef} className="mb-8 flex w-full flex-col gap-4 px-4">
        {isMigratedList && (
          <Alert
            type="information"
            title="Migrated price lists cannot be edited"
            message="This pricing is migrated from your old price tiers. It's still in use for any customers you have assigned to it. You can delete it and reassign these customers once you've created a new price list to replace it."
          />
        )}
        <div>
          <div
            className={twMerge(
              "flex flex-col rounded-lg border border-zinc-300 bg-zinc-50",
              !isBasePrice && "rounded-b-none",
            )}
          >
            <SpotlightFilterContainerCard
              className="rounded-none border-0 bg-transparent"
              desktopClassName="bg-transparent"
              sticky={false}
              mobileBorder={true}
              includeActiveToggle={true}
              moreFilters={
                showMissingPriceAlert ? (
                  <div className="flex w-full items-center justify-between gap-2">
                    <span>Only show products without a price</span>
                    <Switch
                      checked={filterNoPriceSet}
                      onChange={(checked) => setFilterNoPriceSet(checked)}
                    />
                  </div>
                ) : (
                  <></>
                )
              }
            >
              <InputContainer className="w-full flex-none py-[0.4rem] sm:max-w-[280px]">
                <Icon icon="MAGNIFY" />
                <InputText
                  required={false}
                  placeholder="Search products..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full"
                />
              </InputContainer>
            </SpotlightFilterContainerCard>
            {perItemDiscountsCount > 0 && !isBasePrice && !isMigratedList && (
              <div className="p-4 pt-0">
                <AlertCompact
                  type="information"
                  className="w-full text-sm md:w-auto"
                  onClick={() =>
                    setFilterPerItemDiscounts(!filterPerItemDiscounts)
                  }
                >
                  <div className="flex w-full items-center justify-between">
                    {filterPerItemDiscounts ? (
                      <span>
                        Showing{" "}
                        <span className="font-bold">
                          {perItemDiscountsCount}{" "}
                          {pluralize("item", perItemDiscountsCount)}
                        </span>{" "}
                        in this price list with per-item discounts applied
                      </span>
                    ) : (
                      <span className="font-bold">
                        {perItemDiscountsCount}{" "}
                        {pluralize("item", perItemDiscountsCount)}{" "}
                        <span className="font-normal">
                          in this price list have per-item discounts applied
                        </span>
                      </span>
                    )}
                    <span className="flex items-center font-bold text-indigo-800 md:ml-2">
                      {filterPerItemDiscounts ? "Show all items" : "View items"}{" "}
                      <Icon icon="CHEVRON_RIGHT" />
                    </span>
                  </div>
                </AlertCompact>
              </div>
            )}
            {showMissingPriceAlert && (
              <>
                <div className="p-4 pt-0">
                  <AlertCompact
                    type="information"
                    className="w-full text-sm md:w-auto"
                    onClick={() => setFilterNoPriceSet(!filterNoPriceSet)}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span>
                        <span className="font-semibold">
                          {nbMissingPrices}{" "}
                          {pluralize("product", nbMissingPrices)}{" "}
                        </span>
                        in this base price list don't have a price set against
                        them.{" "}
                        <span
                          className="inline-flex cursor-pointer items-center gap-1 text-blue-600 underline"
                          onClick={() => setWhyPriceActive(true)}
                        >
                          Help
                          <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
                        </span>
                      </span>
                      <span className="flex items-center font-bold text-indigo-800 md:ml-2">
                        {filterNoPriceSet
                          ? "Show all products"
                          : "View products"}{" "}
                        <Icon icon="CHEVRON_RIGHT" />
                      </span>
                    </div>
                  </AlertCompact>
                </div>
                <WhyPriceDialog
                  active={whyPriceActive}
                  setActive={setWhyPriceActive}
                />
              </>
            )}
          </div>
          {!isBasePrice && isDesktop && (
            <ColumnHeaders
              gridClasses={gridClasses}
              cellClasses={cellClasses}
              readOnly={!!readOnly}
              isMobile={false}
            />
          )}
        </div>
        <InfiniteList
          query={productListQuery}
          className={twMerge(
            "flex flex-col pb-40 text-sm",
            readOnly && "pointer-events-none",
          )}
        >
          {productList.map((parent, idx) => (
            <div
              className={twMerge(
                "flex flex-col overflow-auto border border-b-0 border-zinc-300 bg-zinc-50",
                idx === 0 && "rounded-t-lg",
                idx === productList.length - 1 && "rounded-b-lg",
              )}
              key={parent.id}
            >
              <div
                className={twMerge(
                  "sticky left-0 flex w-full items-center gap-2 bg-zinc-50 px-4 py-3",
                  idx === 0 && "rounded-t-lg",
                )}
              >
                <ProfilePicture logoUrl={parent.imageUrl} size="small" />
                <span className="font-semibold">{parent.name} • </span>
                <span className="text-zinc-500">
                  {parent.kind}
                  {" // "}
                  {parent.abv}%
                </span>

                {!parent.active && (
                  <span className="ml-auto inline-flex">
                    <InactiveBadge productId={parent.id} />
                  </span>
                )}
              </div>
              {!isBasePrice && !isDesktop && (
                <ColumnHeaders
                  gridClasses={gridClasses}
                  cellClasses={cellClasses}
                  readOnly={!!readOnly}
                  noBorder={true}
                  isMobile={true}
                />
              )}
              <div
                className={twMerge(
                  "grid",
                  parent.active ? "bg-white" : "bg-zinc-50",
                  gridClasses,
                  idx === productList.length - 1 && "rounded-b-lg border-b",
                )}
              >
                {parent.Products.map((product, idxp) => {
                  const pricing = priceListPricingMap[product.id];
                  const active = parent.active;

                  return isBasePrice ? (
                    <>
                      <span
                        id={`product-${product.id}`}
                        className={twMerge(
                          cellClasses,
                          active ? "bg-white" : "bg-zinc-50",
                          idxp === 0 && "border-t",
                          idxp !== parent.Products.length - 1 && "border-b",
                        )}
                      >
                        {getName(product)}
                      </span>
                      <span
                        className={twMerge(
                          cellClasses,
                          "py-1 pr-4",
                          active ? "bg-white" : "bg-zinc-50",
                          idxp === 0 && "border-t",
                          idxp !== parent.Products.length - 1 && "border-b",
                        )}
                      >
                        <CustomPriceListInput
                          inputRefs={inputRefs}
                          productList={productList}
                          product={product}
                          parent={parent}
                          productIndex={idxp}
                          parentIndex={idx}
                          setInputRef={setInputRef}
                          price={
                            pricing?.price || pricing?.price === 0
                              ? formatPriceApiToInput(pricing.price)
                              : undefined
                          }
                          onUpdatePrice={updatePrice}
                        />
                      </span>
                    </>
                  ) : (
                    <>
                      <span
                        id={`product-${product.id}`}
                        className={twMerge(
                          cellClasses,
                          "min-h-[46px]",
                          active ? "bg-white" : "bg-zinc-50",
                          idxp === 0 && "border-t",
                          idxp !== parent.Products.length - 1 && "border-b",
                        )}
                      >
                        {getName(product)}
                      </span>

                      {!readOnly && (
                        <span
                          className={twMerge(
                            cellClasses,
                            "py-1",
                            active ? "bg-white" : "bg-zinc-50",
                            idxp === 0 && "border-t",
                            idxp !== parent.Products.length - 1 && "border-b",
                          )}
                        >
                          <InputContainer className="w-full py-2">
                            <InputSelect
                              className="w-full"
                              value={pricing?.derivationType}
                              onChange={(e) =>
                                handleRuleChange(
                                  product.id,
                                  e.target
                                    .value as ProductPriceTierDerivationType,
                                )
                              }
                            >
                              {derivationTypes.map((dt) => (
                                <InputOption key={dt.type} value={dt.type}>
                                  {dt.display}
                                </InputOption>
                              ))}
                            </InputSelect>
                          </InputContainer>
                        </span>
                      )}
                      <span
                        className={twMerge(
                          cellClasses,
                          "gap-2 font-normal text-zinc-500",
                          active ? "bg-white" : "bg-zinc-50",
                          idxp === 0 && "border-t",
                          idxp !== parent.Products.length - 1 && "border-b",
                          pricing?.basePrice !== pricing?.price &&
                            "line-through",
                        )}
                      >
                        {pricing?.basePrice || pricing?.basePrice === 0
                          ? formatCurrency(pricing.basePrice)
                          : "-"}
                        {[null, undefined, -1].includes(pricing?.basePrice) &&
                          !isMigratedList && (
                            <InfoBadge
                              infoContent={{
                                title: "Base price missing",
                                body: (
                                  <span>
                                    You need to update this products price on
                                    the base price list
                                  </span>
                                ),
                                icon: "ALERT",
                                iconClassName: "text-yellow-600",
                              }}
                            />
                          )}
                      </span>

                      <span
                        className={twMerge(
                          cellClasses,
                          "flex items-center justify-between pr-6",
                          active ? "bg-white" : "bg-zinc-50",
                          idxp === 0 && "border-t",
                          idxp !== parent.Products.length - 1 && "border-b",
                        )}
                      >
                        {pricing?.price || pricing?.price === 0
                          ? formatCurrency(pricing.price)
                          : "-"}
                        {!readOnly &&
                          ["flat", "custom", "percentage"].includes(
                            pricing?.derivationType ?? "",
                          ) && (
                            <Button
                              size={"small"}
                              color={"transparent"}
                              onClick={() => {
                                setSelectedPricing(pricing);
                                setRuleDialogActive(true);
                              }}
                            >
                              <Icon
                                icon={"PENCIL"}
                                className="text-black"
                                size={0.7}
                              />
                            </Button>
                          )}
                      </span>
                      {!readOnly && (
                        <span
                          className={twMerge(
                            "flex items-center justify-center py-3",
                            active ? "bg-white" : "bg-zinc-50",
                            idxp === 0 && "border-t",
                            idxp !== parent.Products.length - 1 && "border-b",
                          )}
                        >
                          <input
                            className="mr-4"
                            type="checkbox"
                            checked={pricing?.discountable}
                            onChange={() => toggleDiscountable(product.id)}
                          />
                        </span>
                      )}
                      {!readOnly && discountsPreviewEnabled && (
                        <span
                          className={twMerge(
                            "flex items-center justify-center bg-zinc-50 py-3",
                            idxp === 0 && "border-t",
                            idxp !== parent.Products.length - 1 && "border-b",
                          )}
                        >
                          <PreviewCell
                            product={{
                              ...product,
                              price: pricing?.price ?? 0,
                              discountable: pricing?.discountable ?? true,
                            }}
                          />
                        </span>
                      )}
                    </>
                  );
                })}
              </div>
            </div>
          ))}
        </InfiniteList>
      </div>
      <PriceRuleUpdateDialog
        active={ruleDialogActive}
        setActive={setRuleDialogActive}
        pricing={selectedPricing}
        updateDerivedPrice={updateDerivedPrice}
      />
    </>
  );
};

const StorefrontPreviewerButton = () => {
  const {
    openStorefrontPreviewer,
    businessName,
    featureFlags,
    customerZones,
    isSearchingForCustomer,
    isSearchingForCustomerZone,
    customers,
    orderRule,
    selectedCustomer,
    productListQuery,
    tab,
    selectedCustomerZone,
    setSelectedCustomerZone,
    setOpenStorefrontPreviewer,
    setSelectedCustomer,
    setCustomerSearchTerm,
    setCustomerZoneSearchTerm,
    onChangeTab,
    onPreviewStorefront,
  } = useStorefrontPreviewerParameters({
    initialTab: StorefrontPreviewerTabs.CUSTOMER_ZONE,
  });

  return (
    <StorefrontPreviewer
      open={openStorefrontPreviewer}
      initialTab={tab}
      trigger={
        <button
          type="button"
          onClick={() => {
            setOpenStorefrontPreviewer(true);
          }}
          color="transparent"
          className="flex w-full cursor-pointer items-center justify-between rounded-none border-t border-t-zinc-200 bg-white p-4 hover:bg-zinc-50"
        >
          <span className="flex items-center gap-2">
            <ListItemIconTile icon="STOREFRONT_OUTLINE" />
            <span className="font-semibold capitalize">Storefront preview</span>
          </span>
          <span>
            <Icon icon="CHEVRON_RIGHT" />
          </span>
        </button>
      }
      businessName={businessName}
      featureFlags={featureFlags}
      customerZoneSearchProps={{
        customerZones,
        isSearchingForCustomerZone,
        selectedCustomerZone,
        onSelectCustomerZone: setSelectedCustomerZone,
        onSearchForCustomerZone: setCustomerZoneSearchTerm,
      }}
      customerSearchProps={{
        customers,
        isSearchingForCustomer,
        selectedCustomer,
        onSelectCustomer: setSelectedCustomer,
        onSearchForCustomer: setCustomerSearchTerm,
      }}
      onTogglePreviewer={setOpenStorefrontPreviewer}
      orderRule={{
        name: orderRule?.name ?? "",
        minimumOrderValue: orderRule?.minimumOrderPrice ?? 0,
        minimumOrderQuantities: orderRule?.PriceTierOrderRules ?? [],
      }}
      productListQuery={productListQuery}
      onClickStorefrontPreview={onPreviewStorefront}
      onChangeTab={onChangeTab}
    />
  );
};

const ColumnHeaders = ({
  gridClasses,
  cellClasses,
  readOnly,
  noBorder = false,
  isMobile,
}: {
  gridClasses: string;
  cellClasses: string;
  readOnly: boolean;
  noBorder?: boolean;
  isMobile: boolean;
}) => {
  const {
    discountsPreviewEnabled,
    discountsPreviewType,
    numberOfUnits,
    minimumSpend,
  } = usePriceListContext();

  return (
    <div
      className={twMerge(
        "grid border-zinc-300 bg-zinc-50 text-xs",
        gridClasses,
        !noBorder && "rounded-b-lg border border-t-0",
      )}
    >
      <span className={twMerge(cellClasses, "py-3")}>Format</span>
      {!readOnly && (
        <span className={twMerge(cellClasses, "py-2")}>Price rule</span>
      )}
      <span className={twMerge(cellClasses, "py-2")}>Base price</span>
      <span className={twMerge(cellClasses, "py-2")}>
        {readOnly ? "Price" : "1 unit price"} (ex. VAT)
      </span>
      {!readOnly && (
        <span className={twMerge(cellClasses, "py-2")}>Discountable</span>
      )}
      {!readOnly && discountsPreviewEnabled && (
        <span
          className={twMerge(
            cellClasses,
            "gap-2 rounded-br-lg bg-zinc-100 py-1 font-semibold",
            isMobile && "z-10 -mt-12 pt-12",
          )}
        >
          {discountsPreviewType === "spend" ? (
            <>{formatCurrency(minimumSpend ?? 0)} spend</>
          ) : (
            <>
              {numberOfUnits} {pluralize("unit", numberOfUnits ?? 1)}
            </>
          )}
        </span>
      )}
    </div>
  );
};

const PreviewCell = ({
  product,
}: {
  product: {
    volume: number | null;
    containerType: string | null;
    price: number;
    discountable: boolean;
    unit: string | null;
    packQuantity: number | null;
  };
}) => {
  const { numberOfUnits, minimumSpend, selectedDiscount } =
    usePriceListContext();

  const units = numberOfUnits ?? Math.ceil((minimumSpend ?? 1) / product.price);
  const discountedPrice = calculatePriceTierDiscount({
    orderItems: [
      {
        ...product,
        quantity: units,
      },
    ],
    priceTierDiscount: selectedDiscount,
    caseSizeRuleList: [],
  });

  const applicable = getContainerTypes(product.containerType).includes(
    selectedDiscount?.itemType ?? "",
  );

  return (
    <span className="flex w-full items-center justify-between px-4">
      {(applicable || selectedDiscount?.itemType === null) &&
        product.discountable && (
          <>
            {formatCurrency(
              product.price -
                (Number.isNaN(discountedPrice) ? 0 : discountedPrice) / units,
            )}{" "}
            /unit
            <Icon
              icon="CIRCLE"
              className="ml-auto text-purple-500"
              size={0.5}
            />
          </>
        )}
    </span>
  );
};

type CustomPriceListInputProps = {
  productList: ProductAssignmentListResponseType;
  parent: ProductAssignmentListResponseType[number];
  product: ProductAssignmentListResponseType[number]["Products"][number];
  inputRefs: RefObject<Record<string, HTMLInputElement | null>>;
  price: string | undefined;
  productIndex: number;
  parentIndex: number;
  onUpdatePrice: (parameters: {
    productId: number;
    price: number | null;
  }) => void;
  setInputRef: (params: {
    productId: number;
    el: HTMLInputElement | null;
  }) => void;
};
const CustomPriceListInput = ({
  product,
  parent,
  productList,
  inputRefs,
  productIndex,
  parentIndex,
  price,
  onUpdatePrice,
  setInputRef,
}: CustomPriceListInputProps) => {
  const [draftPrice, setDraftPrice] = useState<string | undefined>(undefined);

  const value = draftPrice ?? price;

  return (
    <InputContainer className="w-full py-2">
      <InputCurrency
        showPrefix
        allowNegative
        ref={(el) => {
          setInputRef({ productId: product.id, el });
        }}
        value={value}
        onChange={(e) => {
          const newValue = e.target.value;
          setDraftPrice(newValue);

          const formattedPrice = formatPriceInputToApi(newValue);

          if (formattedPrice !== null) {
            onUpdatePrice({
              productId: product.id,
              price: formattedPrice,
            });
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const nextProduct =
              parent.Products[productIndex + 1]?.id ??
              productList[parentIndex + 1]?.Products[0]?.id ??
              "";
            const nextInput = inputRefs.current[nextProduct];
            if (nextInput) {
              nextInput.focus(); // move to next input
            }
          }
        }}
        placeholder="0.00"
        prefix="£"
        min={0}
        className="font-normal"
      />
    </InputContainer>
  );
};
